import React from "react";
import { Banner, Button, Select } from "@/ds";
import { cockpit, type OwnerWorkflowSettings } from "@/api";
import { useT } from "../i18n";
import { sameWorkflowProfile } from "../lib/workflow-profile";

/** Selection is server-owned and affects new requests only. */
export function TenantWorkflow({ tenantId }: { tenantId: string }) {
  const t = useT();
  const [data, setData] = React.useState<OwnerWorkflowSettings | null>(null);
  const [engine, setEngine] = React.useState("deterministic");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);
  const sequence = React.useRef(0);
  const load = React.useCallback(async () => {
    const current = ++sequence.current;
    setBusy(true); setError(null); setSaved(false);
    try {
      const next = await cockpit.getTenantWorkflow(tenantId);
      if (current !== sequence.current) return;
      setData(next); setEngine(next.default_pin.engine);
    } catch (e: any) {
      if (current === sequence.current) setError(e?.status === 403 ? t("workflow.forbidden") : t("workflow.loadFailed"));
    } finally { if (current === sequence.current) setBusy(false); }
  }, [tenantId, t]);
  React.useEffect(() => { setData(null); void load(); return () => { sequence.current++; }; }, [load]);
  const target = data?.options.find(option => option.engine === engine);
  const save = async () => {
    if (!data || !target?.available || !target.profile) return;
    const current = ++sequence.current;
    setBusy(true); setError(null); setSaved(false);
    try {
      const next = await cockpit.setTenantWorkflow(tenantId, { engine: engine as "deterministic" | "single_agent",
        profile_hash: target.profile.profile_hash, expected_version: data.version });
      if (current !== sequence.current) return;
      setData(next); setEngine(next.default_pin.engine); setSaved(true);
    } catch (e: any) {
      if (current === sequence.current) setError(e?.status === 403 ? t("workflow.forbidden")
        : e?.status === 409 ? t("workflow.conflict") : t("workflow.saveFailed"));
    } finally { if (current === sequence.current) setBusy(false); }
  };
  return <section className="stack-sm" aria-label={t("workflow.title")} aria-busy={busy}>
    <h3 style={{ margin: 0 }}>{t("workflow.title")}</h3>
    <p className="subtle">{t("workflow.futureOnly")}</p>
    {error && <Banner tone="danger" title={t("common.error")}>{error}</Banner>}
    {!data && !error && <p>{t("common.loading")}</p>}
    {data && <>
      <p className="subtle">{t("workflow.mode")}: {t(`workflow.${data.default_pin.engine}`)} · {data.default_pin.profile_id} {data.default_pin.profile_version}</p>
      <label className="stack-sm">
        <span>{t("workflow.default")}</span>
        <Select value={engine} disabled={busy || !data.can_update}
          onChange={(event) => { setEngine(event.target.value); setSaved(false); }}>
          {data.options.map((option) => <option key={option.engine} value={option.engine} disabled={!option.available}>
            {t(`workflow.${option.engine}`)}{!option.available ? ` — ${t("workflow.unavailable")}` : ""}
          </option>)}
        </Select>
      </label>
      {target?.profile && <p className="subtle">{t("workflow.target")}: {target.profile.profile_id} {target.profile.profile_version}</p>}
      {data.options.filter((option) => !option.available).map((option) => <p className="subtle" key={option.engine}>
        {t(`workflow.${option.engine}`)}: {t(`workflow.error.${option.reason}`, undefined, t("workflow.notReady"))}
      </p>)}
      <div className="row" style={{ gap: "var(--space-2)", flexWrap: "wrap" }}>
        <Button size="sm" disabled={busy || !data.can_update || !target?.available || !target.profile || sameWorkflowProfile(target.profile, data.default_pin)} onClick={() => { void save(); }}>{t("workflow.save")}</Button>
        <Button size="sm" variant="secondary" disabled={busy} onClick={() => { void load(); }}>{t("workflow.reload")}</Button>
      </div>
      {saved && <p role="status">{t("workflow.saved")}</p>}
    </>}
    {!data && error && <Button size="sm" variant="secondary" disabled={busy} onClick={() => { void load(); }}>{t("workflow.reload")}</Button>}
  </section>;
}
